/**
 * Provider Transformation Service
 * 
 * Handles all data transformations between API and form formats for providers.
 * This service ensures consistency across all provider management operations.
 * 
 * @module ProviderTransformationService
 */

import { getDepartmentsByFacilityId, getFacilities } from '../utils/apiClient';

/**
 * Transforms form data (UI format) to API format
 * 
 * @param {Object} formData - Form data with affiliations structure
 * @returns {Object} API formatted data
 */
export const transformFormToAPI = (formData) => {
  return {
    ...formData,
    // Ensure affiliations have proper structure with new format
    affiliations: formData.affiliations?.map(affiliation => {
      const transformed = {
        facilityId: affiliation.facilityId,
        role: Array.isArray(affiliation.role) ? affiliation.role : []
      };
      
      // Only include departmentId if it exists and is not empty
      if (affiliation.departmentId && affiliation.departmentId.trim() !== '') {
        transformed.departmentId = affiliation.departmentId;
      }
      
      return transformed;
    }).filter(affiliation => 
      // Filter out affiliations without facilityId or roles
      affiliation.facilityId && affiliation.facilityId.trim() !== '' &&
      affiliation.role && affiliation.role.length > 0
    ) || []
  };
};

/**
 * Transforms API data to form format for editing
 * Handles both old and new affiliation data structures
 * Filters out inactive facilities and departments before loading into form
 * 
 * @param {Object} providerData - Provider data from API
 * @param {Function} setFacilitiesDepartments - State setter for departments cache
 * @returns {Promise<Object>} Form formatted data
 */
export const transformAPIToForm = async (providerData, setFacilitiesDepartments) => {
  if (!providerData.affiliations || providerData.affiliations.length === 0) {
    return {
      ...providerData,
      affiliations: [{ facilityId: '', role: [] }],
    };
  }

  try {
    // Fetch active facilities list
    let activeFacilityIds = new Set();
    try {
      const facilitiesResponse = await getFacilities();
      if (facilitiesResponse && Array.isArray(facilitiesResponse)) {
        activeFacilityIds = new Set(facilitiesResponse.map(f => f._id).filter(Boolean));
        console.log('✅ Active facilities loaded:', activeFacilityIds.size);
      }
    } catch (error) {
      console.warn('Failed to fetch active facilities:', error);
    }

    // Fetch departments for each facility in affiliations
    const facilityDepartmentsMap = {};
    const uniqueFacilities = new Set();
    
    // Collect all unique facility IDs (only active ones)
    for (const affiliation of providerData.affiliations) {
      if (affiliation.facilityId && activeFacilityIds.has(affiliation.facilityId)) {
        uniqueFacilities.add(affiliation.facilityId);
      }
    }

    // Fetch departments for each unique active facility
    for (const facilityId of uniqueFacilities) {
      try {
        const response = await getDepartmentsByFacilityId(facilityId);
        if (response.statusCode === 'SUCCESS' && Array.isArray(response.response)) {
          facilityDepartmentsMap[facilityId] = response.response;
        }
      } catch (error) {
        console.warn(`Failed to fetch departments for facility ${facilityId}:`, error);
        facilityDepartmentsMap[facilityId] = [];
      }
    }

    // Update departments cache
    if (setFacilitiesDepartments) {
      setFacilitiesDepartments(prev => ({
        ...prev,
        ...facilityDepartmentsMap
      }));
    }

    // Transform affiliations to new format and filter out inactive facilities/departments
    let transformedAffiliations = [];

    for (const affiliation of providerData.affiliations) {
      // Skip affiliation if facility is inactive
      if (!activeFacilityIds.has(affiliation.facilityId)) {
        console.log(`⚠️ Skipping inactive facility: ${affiliation.facilityId}`);
        continue;
      }

      if (affiliation.departments && Array.isArray(affiliation.departments)) {
        // Old format: convert departments array to separate affiliations
        if (affiliation.departments.length === 0) {
          // Facility-level affiliation (no departments)
          transformedAffiliations.push({
            facilityId: affiliation.facilityId,
            role: Array.isArray(affiliation.role) ? affiliation.role : []
          });
        } else {
          // Create separate affiliation for each department
          affiliation.departments.forEach(departmentId => {
            // Check if department is active
            const departments = facilityDepartmentsMap[affiliation.facilityId] || [];
            const department = departments.find(d => d._id === departmentId);
            
            if (!department) {
              console.log(`⚠️ Department not found: ${departmentId} in facility ${affiliation.facilityId}`);
              return; // Skip this department
            }
            
            if (department.isActive === false) {
              console.log(`⚠️ Skipping inactive department: ${departmentId} (${department.name})`);
              return; // Skip this department
            }
            
            // Department is active, add it
            transformedAffiliations.push({
              facilityId: affiliation.facilityId,
              departmentId: departmentId,
              role: Array.isArray(affiliation.role) ? affiliation.role : []
            });
          });
        }
      } else {
        // New format: already in correct structure
        const transformed = {
          facilityId: affiliation.facilityId,
          role: Array.isArray(affiliation.role) ? affiliation.role : []
        };
        
        // Check if department exists and is active
        if (affiliation.departmentId) {
          const departments = facilityDepartmentsMap[affiliation.facilityId] || [];
          const department = departments.find(d => d._id === affiliation.departmentId);
          
          if (department && department.isActive !== false) {
            // Department is active, include it
            transformed.departmentId = affiliation.departmentId;
          } else if (department && department.isActive === false) {
            // Department is inactive, skip departmentId (convert to facility-level)
            console.log(`⚠️ Removing inactive department ${affiliation.departmentId} (${department.name}) from affiliation`);
          } else if (!department) {
            // Department not found, skip departmentId
            console.log(`⚠️ Department not found: ${affiliation.departmentId}, converting to facility-level`);
          }
        }
        
        transformedAffiliations.push(transformed);
      }
    }

    console.log(`✅ Filtered affiliations: ${transformedAffiliations.length} active out of ${providerData.affiliations.length} total`);

    return {
      ...providerData,
      affiliations: transformedAffiliations.length > 0 ? transformedAffiliations : [{ facilityId: '', role: [] }],
    };
  } catch (error) {
    console.error('Error transforming API data for form:', error);
    return {
      ...providerData,
      affiliations: [{ facilityId: '', role: [] }],
    };
  }
};

/**
 * Default service export
 */
const ProviderTransformationService = {
  transformFormToAPI,
  transformAPIToForm
};

export default ProviderTransformationService;